import React, { useCallback, useEffect, useState, useRef, Fragment, Component } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, X, Loader2, Users, Filter, ChevronDown, Trash2, Eye, Search, Target, Zap, Hash, Type, ToggleLeft, List, AlertTriangle, CheckCircle2, RefreshCw } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { crmContactsApi, CrmContact } from '../services/crmApi';
import { auditService } from '../services/auditService';
import { audienceStorage, discoverPayloadFields, filterContactsByRules, STANDARD_FIELDS, discoverCrmFieldsFromContacts, OPERATOR_LABELS, getOperatorsForFieldType, generateId, createEmptyRule, DiscoveredField, AudienceRule, SavedAudience, StandardFieldDef } from '../services/audienceApi';
import { toast } from 'sonner';
// ── Field type icons ──
function FieldTypeIcon({
  type,
  className
}: {
  type: string;
  className?: string;
}) {
  const cls = className || 'h-3.5 w-3.5';
  switch (type) {
    case 'boolean':
      return <ToggleLeft className={cls} data-id="element-2504" />;
    case 'select':
      return <List className={cls} data-id="element-2505" />;
    case 'number':
      return <Hash className={cls} data-id="element-2506" />;
    default:
      return <Type className={cls} data-id="element-2507" />;
  }
}
// ── Contact Preview Panel ──
function ContactPreviewPanel({
  contacts,
  onClose
}: {
  contacts: CrmContact[];
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [visibleCount, setVisibleCount] = useState(100);
  const filtered = search.trim() ? contacts.filter(c => {
    const q = search.toLowerCase();
    const name = (c.Full_Name || `${c.First_Name || ''} ${c.Last_Name || ''}`).toLowerCase();
    const email = (c.Email || '').toLowerCase();
    return name.includes(q) || email.includes(q);
  }) : contacts;
  const visible = filtered.slice(0, visibleCount);
  return <div className="fixed inset-0 z-50 overflow-hidden" data-id="element-2508">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-id="element-2509" />
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white shadow-xl flex flex-col" data-id="element-2510">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200" data-id="element-2511">
          <div data-id="element-2512">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-2513">
              Matching Contacts
            </h2>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-2514">
              {contacts.length} contact{contacts.length !== 1 ? 's' : ''} match
              your rules
              {search.trim() && filtered.length !== contacts.length && <span data-id="element-2515"> · {filtered.length} matching search</span>}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100" data-id="element-2516">
            <X className="h-5 w-5" data-id="element-2517" />
          </button>
        </div>

        <div className="px-6 py-3 border-b border-gray-100" data-id="element-2518">
          <div className="relative" data-id="element-2519">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2520" />
            <input type="text" value={search} onChange={e => {
            setSearch(e.target.value);
            setVisibleCount(100);
          }} placeholder="Search by name or email..." className="block w-full pl-9 pr-4 py-2 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2521" />
          </div>
        </div>

        <div className="flex-1 overflow-auto" data-id="element-2522">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-2523">
            <thead className="bg-gray-50 sticky top-0" data-id="element-2524">
              <tr data-id="element-2525">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2526">
                  #
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2527">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2528">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2529">
                  Region
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-2530">
              {visible.map((contact, index) => <tr key={contact.id || index} data-id="element-2531">
                  <td className="px-6 py-3 whitespace-nowrap text-xs text-gray-400" data-id="element-2532">
                    {index + 1}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-2533">
                    {contact.Full_Name || `${contact.First_Name || ''} ${contact.Last_Name || ''}`.trim() || <span className="text-gray-400" data-id="element-2534">—</span>}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2535">
                    {contact.Email || <span className="text-gray-400" data-id="element-2536">—</span>}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-600" data-id="element-2537">
                    {contact.Sub_Region || contact.Region || <span className="text-gray-400" data-id="element-2538">—</span>}
                  </td>
                </tr>)}
              {visible.length === 0 && <tr data-id="element-2539">
                  <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500" data-id="element-2540">
                    {search.trim() ? 'No contacts match your search' : 'No matching contacts'}
                  </td>
                </tr>}
            </tbody>
          </table>

          {filtered.length > visibleCount && <div className="px-6 py-4 text-center border-t border-gray-100" data-id="element-2541">
              <button onClick={() => setVisibleCount(prev => prev + 100)} className="text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-2542">
                Show more ({filtered.length - visibleCount} remaining)
              </button>
            </div>}
        </div>
      </div>
    </div>;
}
// ── Rule Row Component ──
function RuleRow({
  rule,
  index,
  crmFields,
  discoveredFields,
  onChange,
  onDelete
}: {
  rule: AudienceRule;
  index: number;
  crmFields: StandardFieldDef[];
  discoveredFields: DiscoveredField[];
  onChange: (updates: Partial<AudienceRule>) => void;
  onDelete: () => void;
}) {
  // Determine field type and available operators
  const standardField = crmFields.find(f => f.key === rule.field);
  const payloadField = discoveredFields.find(f => f.key === rule.field);
  const fieldType = standardField?.type || payloadField?.type || 'text';
  const operators = standardField ? standardField.operators : getOperatorsForFieldType(fieldType);
  const needsValueInput = !['is_true', 'is_false', 'is_empty', 'is_not_empty'].includes(rule.operator);
  const isSelectField = fieldType === 'select' && payloadField?.values;
  return <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2543">
      <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2" data-id="element-2544">
        {/* Field selector */}
        <div data-id="element-2545">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-2546">
            Field
          </label>
          <select value={rule.field ? `${rule.fieldSource}:${rule.field}` : ''} onChange={e => {
          const val = e.target.value;
          if (!val) {
            onChange({
              field: '',
              fieldSource: 'standard',
              operator: 'equals',
              value: ''
            });
            return;
          }
          const [source, ...keyParts] = val.split(':');
          const key = keyParts.join(':');
          const newSource = source as 'standard' | 'payload';
          // Reset operator to first valid one for the new field type
          const sf = crmFields.find(f => f.key === key);
          const pf = discoveredFields.find(f => f.key === key);
          const ft = sf?.type || pf?.type || 'text';
          const ops = sf ? sf.operators : getOperatorsForFieldType(ft);
          const defaultOp = ops[0] || 'equals';
          onChange({
            field: key,
            fieldSource: newSource,
            operator: defaultOp,
            value: ''
          });
        }} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2547">
            <option value="" data-id="element-2548">Select field...</option>
            <optgroup label="CRM Fields" data-id="element-2549">
              {crmFields.map(f => <option key={f.key} value={`standard:${f.key}`} data-id="element-2550">
                  {f.label}
                </option>)}
            </optgroup>
            {discoveredFields.length > 0 && <optgroup label="Form Data Fields" data-id="element-2551">
                {discoveredFields.map(f => <option key={f.key} value={`payload:${f.key}`} data-id="element-2552">
                    {f.label}
                  </option>)}
              </optgroup>}
          </select>
        </div>

        {/* Operator selector */}
        <div data-id="element-2553">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-2554">
            Condition
          </label>
          <select value={rule.operator} onChange={e => onChange({
          operator: e.target.value as AudienceRule['operator'],
          value: ''
        })} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" disabled={!rule.field} data-id="element-2555">
            {operators.map(op => <option key={op} value={op} data-id="element-2556">
                {OPERATOR_LABELS[op]}
              </option>)}
          </select>
        </div>

        {/* Value input */}
        <div data-id="element-2557">
          <label className="block text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-2558">
            Value
          </label>
          {!needsValueInput ? <div className="h-[38px] flex items-center px-3 text-sm text-gray-400 italic bg-gray-100 rounded-md border border-gray-200" data-id="element-2559">
              No value needed
            </div> : isSelectField ? rule.operator === 'in' ? <div className="space-y-1" data-id="element-2560">
                {(payloadField?.values || []).map(val => {
            const selected = Array.isArray(rule.value) ? rule.value.includes(val) : rule.value === val;
            return <label key={val} className="flex items-center gap-2 text-sm text-gray-700" data-id="element-2561">
                      <input type="checkbox" checked={selected} onChange={() => {
                const current = Array.isArray(rule.value) ? rule.value : rule.value ? [rule.value] : [];
                const next = selected ? current.filter(v => v !== val) : [...current, val];
                onChange({
                  value: next
                });
              }} className="h-3.5 w-3.5 text-blue-600 rounded border-gray-300" data-id="element-2562" />
                      {val}
                    </label>;
          })}
              </div> : <select value={Array.isArray(rule.value) ? rule.value[0] || '' : rule.value} onChange={e => onChange({
          value: e.target.value
        })} className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" data-id="element-2563">
                <option value="" data-id="element-2564">Select value...</option>
                {(payloadField?.values || []).map(val => <option key={val} value={val} data-id="element-2565">
                    {val}
                  </option>)}
              </select> : <input type={fieldType === 'number' ? 'number' : 'text'} value={Array.isArray(rule.value) ? rule.value.join(', ') : rule.value} onChange={e => onChange({
          value: e.target.value
        })} placeholder="Enter value..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm" disabled={!rule.field} data-id="element-2566" />}
        </div>
      </div>

      <button onClick={onDelete} className="mt-5 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded flex-shrink-0" title="Remove rule" data-id="element-2567">
        <X className="h-4 w-4" data-id="element-2568" />
      </button>
    </div>;
}
// ── Main Editor ──
export function AudienceEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion,
    currentUser
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  // Audience state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState<AudienceRule[]>([]);
  const [logic, setLogic] = useState<'AND' | 'OR'>('AND');
  // Data state
  const [allContacts, setAllContacts] = useState<CrmContact[]>([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [loadProgress, setLoadProgress] = useState('');
  // Field discovery state
  const [discoveredFields, setDiscoveredFields] = useState<DiscoveredField[]>([]);
  const [crmFields, setCrmFields] = useState<StandardFieldDef[]>(STANDARD_FIELDS);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryProgress, setDiscoveryProgress] = useState('');
  const [discoveryDone, setDiscoveryDone] = useState(false);
  // Preview state
  const [matchingContacts, setMatchingContacts] = useState<CrmContact[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // Debounce timer ref
  const filterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // ── Reset all contact data when workspace or sub-region changes ──
  // This prevents stale data from a previous scope being used for filtering.
  const prevScopeRef = useRef<string>('');
  useEffect(() => {
    const scopeKey = `${currentWorkspace.name}::${currentSubRegion || ''}`;
    if (prevScopeRef.current && prevScopeRef.current !== scopeKey) {
      // Scope changed — reset everything
      setAllContacts([]);
      setContactsLoaded(false);
      setMatchingContacts([]);
      setDiscoveredFields([]);
      setDiscoveryDone(false);
      setLoadProgress('');
    }
    prevScopeRef.current = scopeKey;
  }, [currentWorkspace.name, currentSubRegion]);
  // Load existing audience
  useEffect(() => {
    if (isEditing && id) {
      const loadAudience = async () => {
        // Try localStorage first (fast)
        let existing = audienceStorage.get(id);
        // If not in localStorage, try syncing from Creator
        if (!existing) {
          try {
            await audienceStorage.syncFromCreator(currentWorkspace.name);
            existing = audienceStorage.get(id);
          } catch (err) {
            console.warn('Failed to sync audience from Creator:', err);
          }
        }
        if (existing) {
          setName(existing.name);
          setDescription(existing.description);
          setRules(existing.rules);
          setLogic(existing.logic);
        } else {
          toast.error('Audience not found');
          navigate('/audiences');
        }
      };
      loadAudience();
    }
  }, [id, isEditing, navigate, currentWorkspace.name]);
  // Load contacts for audience filtering — uses cached data if available
  const loadContacts = useCallback(async () => {
    // If contacts are already loaded (from Analyze Form Data), just apply filters
    if (contactsLoaded && allContacts.length > 0) return allContacts;
    setLoadingContacts(true);
    setLoadProgress('Loading contacts (200 per batch)...');
    try {
      // Pass team/region filters so irrelevant contacts are skipped during pagination
      // Only pass region if a specific sub-region is selected (not "All Regions")
      const scopedContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
        setLoadProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched from CRM, ${matching.toLocaleString()} in scope...`);
      }, {
        team: currentWorkspace.name,
        region: currentSubRegion || undefined
      });
      setAllContacts(scopedContacts);
      setContactsLoaded(true);
      setLoadProgress('');
      // Discover CRM fields from loaded contacts
      const discovered = discoverCrmFieldsFromContacts(scopedContacts);
      setCrmFields(discovered);
      return scopedContacts;
    } catch (err) {
      console.error('Failed to load contacts:', err);
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to load contacts: ${msg}`);
      setLoadProgress('');
      return [];
    } finally {
      setLoadingContacts(false);
    }
  }, [currentWorkspace.name, currentSubRegion, contactsLoaded, allContacts]);
  // Discover fields — fetches ALL contacts, caches them, then analyzes form data
  // This is the single data-loading step. "Load Matching Contacts" reuses this cache.
  const handleDiscoverFields = async () => {
    setIsDiscovering(true);
    setDiscoveryProgress('Loading all contacts (200 per batch)...');
    try {
      // Fetch ALL contacts filtered by team/region — this serves double duty:
      // 1. Discover form fields from contacts with Form_Payload
      // 2. Cache contacts so "Load Matching Contacts" is instant (no re-fetch)
      const scopedContacts = await crmContactsApi.listAll((matching, totalFetched, page) => {
        setDiscoveryProgress(`Page ${page}: ${totalFetched.toLocaleString()} fetched from CRM, ${matching.toLocaleString()} in scope...`);
      }, {
        team: currentWorkspace.name,
        region: currentSubRegion || undefined
      });
      // Cache ALL contacts for later filtering
      setAllContacts(scopedContacts);
      setContactsLoaded(true);
      // Discover CRM fields from all contacts
      const crmDiscovered = discoverCrmFieldsFromContacts(scopedContacts);
      setCrmFields(crmDiscovered);
      setDiscoveryProgress(`Loaded ${scopedContacts.length.toLocaleString()} contacts. Scanning for form data...`);
      // Filter for contacts with Form_Payload to discover form fields
      const formContacts = scopedContacts.filter(c => {
        const payload = (c as any).Form_Payload;
        return payload && (typeof payload !== 'string' || payload.trim() !== '');
      });
      // Collect Form_Payload values
      const payloads: string[] = formContacts.map(c => (c as any).Form_Payload).filter(Boolean);
      if (payloads.length === 0) {
        toast.warning('No form payload data found on contacts', {
          description: 'Contacts need to have Form_Payload data from signup form submissions.'
        });
        setDiscoveryDone(true);
        setIsDiscovering(false);
        setDiscoveryProgress('');
        return;
      }
      setDiscoveryProgress(`Found ${formContacts.length.toLocaleString()} contacts with form data. Analyzing ${payloads.length.toLocaleString()} submissions...`);
      const fields = discoverPayloadFields(payloads);
      setDiscoveredFields(fields);
      setDiscoveryDone(true);
      if (fields.length > 0) {
        toast.success(`Discovered ${fields.length} filterable form fields`, {
          description: `${scopedContacts.length.toLocaleString()} contacts cached — filter rules will apply instantly.`
        });
        // Auto-add a rule if none exist, to guide the user to the next step
        if (rules.length === 0) {
          setRules([createEmptyRule()]);
        }
      } else {
        toast.info('No filterable fields found in form data');
      }
    } catch (err) {
      console.error('Field discovery failed:', err);
      toast.error(`Field analysis failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsDiscovering(false);
      setDiscoveryProgress('');
    }
  };
  // Apply filters (debounced)
  useEffect(() => {
    if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    const validRules = rules.filter(r => r.field);
    if (validRules.length === 0) {
      setMatchingContacts([]);
      return;
    }
    if (!contactsLoaded) return;
    filterTimerRef.current = setTimeout(() => {
      const result = filterContactsByRules(allContacts, validRules, logic);
      setMatchingContacts(result);
    }, 300);
    return () => {
      if (filterTimerRef.current) clearTimeout(filterTimerRef.current);
    };
  }, [rules, logic, allContacts, contactsLoaded]);
  // Rule handlers
  const addRule = () => {
    setRules(prev => [...prev, createEmptyRule()]);
  };
  const updateRule = (id: string, updates: Partial<AudienceRule>) => {
    setRules(prev => prev.map(r => r.id === id ? {
      ...r,
      ...updates
    } : r));
  };
  const deleteRule = (id: string) => {
    setRules(prev => prev.filter(r => r.id !== id));
  };
  // Save
  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter an audience name');
      return;
    }
    const validRules = rules.filter(r => r.field);
    if (validRules.length === 0) {
      toast.error('Please add at least one filter rule');
      return;
    }
    // Check for incomplete rules — operators that require a value but have none
    const valueRequiringOperators = ['equals', 'not_equals', 'contains', 'not_contains', 'in'];
    const incompleteRules = validRules.filter(r => {
      if (!valueRequiringOperators.includes(r.operator)) return false;
      if (Array.isArray(r.value)) return r.value.filter(Boolean).length === 0;
      return !r.value && r.value !== '0';
    });
    if (incompleteRules.length > 0) {
      toast.error(`${incompleteRules.length} rule${incompleteRules.length !== 1 ? 's' : ''} missing a value. Please complete all rules or remove empty ones.`);
      return;
    }
    setIsSaving(true);
    try {
      const audience: SavedAudience = {
        id: isEditing && id ? id : generateId(),
        name: name.trim(),
        description: description.trim(),
        team: currentWorkspace.name,
        region: currentSubRegion || undefined,
        rules: validRules,
        logic,
        createdAt: isEditing ? audienceStorage.get(id!)?.createdAt || new Date().toISOString() : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lastCount: matchingContacts.length
      };
      const result = await audienceStorage.save(audience);
      if (result.synced) {
        toast.success(isEditing ? 'Audience updated & saved to Creator' : 'Audience created & saved to Creator');
      } else {
        toast.warning(isEditing ? 'Audience updated locally' : 'Audience created locally', {
          description: `Creator sync failed: ${result.error}. Data is saved locally and will sync on next save.`
        });
      }
      // Fire-and-forget: Audit log
      auditService.log({
        actionType: isEditing ? 'updated' : 'created',
        entityType: 'audience',
        entityId: audience.id,
        entityName: audience.name,
        summary: isEditing ? `Updated audience '${audience.name}' — ${validRules.length} rule(s), ${logic} logic, ~${matchingContacts.length} matches` : `Created audience '${audience.name}' — ${validRules.length} rule(s), ${logic} logic, ~${matchingContacts.length} matches`,
        userName: currentUser.User_Name,
        userEmail: currentUser.Email,
        userRole: currentUser.Role,
        team: currentWorkspace.name,
        region: currentSubRegion || '',
        metadata: {
          ruleCount: validRules.length,
          logic,
          matchCount: matchingContacts.length,
          synced: result.synced
        }
      });
      navigate('/audiences');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Failed to save audience: ${msg}`);
    } finally {
      setIsSaving(false);
    }
  };
  const validRuleCount = rules.filter(r => r.field).length;
  return <div className="space-y-6 max-w-4xl mx-auto pb-20" data-id="element-2569">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2570">
        <div className="flex items-center gap-4" data-id="element-2571">
          <Link to="/audiences" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-2572">
            <ArrowLeft className="h-5 w-5" data-id="element-2573" />
          </Link>
          <div data-id="element-2574">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2575">
              {isEditing ? 'Edit Audience' : 'Create Audience'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-2576">
              Build a dynamic audience using CRM fields and form submission
              data.
            </p>
          </div>
        </div>
        <button onClick={handleSave} disabled={isSaving || !name.trim() || validRuleCount === 0} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2577">
          {isSaving ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2578" /> : <Save className="h-4 w-4" data-id="element-2579" />}
          {isSaving ? 'Saving...' : 'Save Audience'}
        </button>
      </div>

      {/* Audience Name */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-2580">
        <div className="grid grid-cols-1 gap-4" data-id="element-2581">
          <div data-id="element-2582">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2583">
              Audience Name
            </label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Physical Attendees in Brazil" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-2584" />
          </div>
          <div data-id="element-2585">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2586">
              Description{' '}
              <span className="text-gray-400 font-normal" data-id="element-2587">(optional)</span>
            </label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={2} placeholder="Describe who this audience targets..." className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" data-id="element-2588" />
          </div>
        </div>
      </div>

      {/* Field Discovery */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-2589">
        <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-amber-50 to-orange-50" data-id="element-2590">
          <div className="flex items-center justify-between" data-id="element-2591">
            <div className="flex items-center gap-3" data-id="element-2592">
              <div className="p-2 bg-amber-100 rounded-lg" data-id="element-2593">
                <Zap className="h-5 w-5 text-amber-600" data-id="element-2594" />
              </div>
              <div data-id="element-2595">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-2596">
                  Form Field Discovery
                </h2>
                <p className="text-xs text-gray-600 mt-0.5" data-id="element-2597">
                  {contactsLoaded ? `${allContacts.length.toLocaleString()} contacts loaded — form fields discovered` : 'Load contacts and discover filterable form fields'}
                </p>
              </div>
            </div>
            <button onClick={handleDiscoverFields} disabled={isDiscovering} className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700 disabled:opacity-50" data-id="element-2598">
              {isDiscovering ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2599" /> : discoveryDone ? <RefreshCw className="h-4 w-4" data-id="element-2600" /> : <Zap className="h-4 w-4" data-id="element-2601" />}
              {isDiscovering ? 'Loading & Analyzing...' : discoveryDone ? 'Re-analyze' : 'Analyze Form Data'}
            </button>
          </div>

          {/* Progress */}
          {isDiscovering && <div className="mt-3 flex items-center gap-2 text-sm text-amber-700" data-id="element-2602">
              <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" data-id="element-2603" />
              <span data-id="element-2604">{discoveryProgress}</span>
            </div>}
        </div>

        {/* Discovered fields */}
        {discoveredFields.length > 0 && <div className="px-6 py-4" data-id="element-2605">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3" data-id="element-2606">
              {discoveredFields.length} Discovered Field
              {discoveredFields.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-id="element-2607">
              {discoveredFields.map(field => <div key={field.key} className="flex items-start gap-2.5 p-3 bg-gray-50 rounded-lg border border-gray-200" data-id="element-2608">
                  <div className="mt-0.5 p-1 bg-white rounded border border-gray-200" data-id="element-2609">
                    <FieldTypeIcon type={field.type} className="h-3.5 w-3.5 text-gray-500" data-id="element-2610" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-2611">
                    <p className="text-sm font-medium text-gray-900 truncate" data-id="element-2612">
                      {field.label}
                    </p>
                    <p className="text-[11px] text-gray-500 font-mono" data-id="element-2613">
                      {field.key}
                    </p>
                    {field.type === 'select' && field.values && <div className="flex flex-wrap gap-1 mt-1.5" data-id="element-2614">
                        {field.values.slice(0, 4).map(v => <span key={v} className="inline-block px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded" data-id="element-2615">
                            {v}
                          </span>)}
                        {field.values.length > 4 && <span className="text-[10px] text-gray-400" data-id="element-2616">
                            +{field.values.length - 4} more
                          </span>}
                      </div>}
                    {field.type === 'boolean' && <span className="inline-block mt-1 px-1.5 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded" data-id="element-2617">
                        Yes / No
                      </span>}
                  </div>
                </div>)}
            </div>
          </div>}

        {discoveryDone && discoveredFields.length === 0 && <div className="px-6 py-4" data-id="element-2618">
            <div className="flex items-center gap-2 text-sm text-gray-500" data-id="element-2619">
              <AlertTriangle className="h-4 w-4 text-amber-500" data-id="element-2620" />
              No filterable form fields found. Contacts may not have
              Form_Payload data yet.
            </div>
          </div>}

        {!discoveryDone && !isDiscovering && <div className="px-6 py-4" data-id="element-2621">
            <p className="text-sm text-gray-500" data-id="element-2622">
              Click <strong data-id="element-2623">Analyze Form Data</strong> to load your contacts and
              discover filterable form fields (checkboxes, dropdowns, text
              fields, and more). Contacts are cached so that filter rules apply
              instantly without re-fetching.
            </p>
          </div>}
      </div>

      {/* Filter Rules */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-2624">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-2625">
          <div className="flex items-center gap-2" data-id="element-2626">
            <Filter className="h-4 w-4 text-gray-400" data-id="element-2627" />
            <h2 className="text-sm font-semibold text-gray-900" data-id="element-2628">
              Filter Rules
            </h2>
            {rules.length > 0 && <span className="text-xs text-gray-500" data-id="element-2629">
                ({validRuleCount} active)
              </span>}
          </div>
          <div className="flex items-center gap-3" data-id="element-2630">
            {/* Logic toggle */}
            {rules.length > 1 && <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5" data-id="element-2631">
                <button onClick={() => setLogic('AND')} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${logic === 'AND' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2632">
                  AND
                </button>
                <button onClick={() => setLogic('OR')} className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${logic === 'OR' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-2633">
                  OR
                </button>
              </div>}
            <button onClick={addRule} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md" data-id="element-2634">
              <Plus className="h-3.5 w-3.5" data-id="element-2635" />
              Add Rule
            </button>
          </div>
        </div>

        <div className="p-6" data-id="element-2636">
          {rules.length === 0 ? <div className="text-center py-8" data-id="element-2637">
              <Filter className="mx-auto h-10 w-10 text-gray-300" data-id="element-2638" />
              <p className="mt-2 text-sm text-gray-500" data-id="element-2639">
                No filter rules yet. Add a rule to start building your audience.
              </p>
              <button onClick={addRule} className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700" data-id="element-2640">
                <Plus className="h-4 w-4" data-id="element-2641" />
                Add First Rule
              </button>
            </div> : <div className="space-y-2" data-id="element-2642">
              {rules.map((rule, index) => <Fragment key={rule.id} data-id="element-2643">
                  {index > 0 && <div className="flex items-center justify-center py-1" data-id="element-2644">
                      <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest px-3 py-0.5 bg-gray-100 rounded-full" data-id="element-2645">
                        {logic}
                      </span>
                    </div>}
                  <RuleRow rule={rule} index={index} crmFields={crmFields} discoveredFields={discoveredFields} onChange={updates => updateRule(rule.id, updates)} onDelete={() => deleteRule(rule.id)} data-id="element-2646" />
                </Fragment>)}
            </div>}
        </div>
      </div>

      {/* Audience Preview */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-2647">
        <div className="flex items-center justify-between" data-id="element-2648">
          <div className="flex items-center gap-3" data-id="element-2649">
            <div className={`p-2 rounded-lg ${matchingContacts.length > 0 ? 'bg-green-50' : 'bg-gray-100'}`} data-id="element-2650">
              <Users className={`h-5 w-5 ${matchingContacts.length > 0 ? 'text-green-600' : 'text-gray-400'}`} data-id="element-2651" />
            </div>
            <div data-id="element-2652">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-2653">
                Audience Preview
              </h2>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-2654">
                {validRuleCount === 0 ? contactsLoaded ? `${allContacts.length.toLocaleString()} contacts cached — add filter rules above to build your audience` : 'Add filter rules above to build your audience' : !contactsLoaded ? `${validRuleCount} rule${validRuleCount !== 1 ? 's' : ''} defined — click "Analyze Form Data" first to load contacts, or click "Load Matching Contacts"` : <>
                    <span className="font-semibold text-gray-900" data-id="element-2655">
                      {matchingContacts.length.toLocaleString()}
                    </span>{' '}
                    contact{matchingContacts.length !== 1 ? 's' : ''} match{' '}
                    {validRuleCount} rule{validRuleCount !== 1 ? 's' : ''} (
                    {logic} logic) out of {allContacts.length.toLocaleString()}{' '}
                    in scope
                  </>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-id="element-2656">
            {!contactsLoaded && <button onClick={loadContacts} disabled={loadingContacts || validRuleCount === 0} title={validRuleCount === 0 ? 'Add at least one filter rule before loading contacts' : ''} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2657">
                {loadingContacts ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-2658" /> : <RefreshCw className="h-4 w-4" data-id="element-2659" />}
                {loadingContacts ? 'Loading...' : 'Load Matching Contacts'}
              </button>}
            {contactsLoaded && !loadingContacts && <button onClick={() => {
            setContactsLoaded(false);
            setAllContacts([]);
            setMatchingContacts([]);
            // Use setTimeout to ensure state is cleared before re-fetching
            setTimeout(() => {
              loadContacts();
            }, 0);
          }} disabled={loadingContacts} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2660">
                <RefreshCw className="h-4 w-4" data-id="element-2661" />
                Refresh from CRM
              </button>}
            {matchingContacts.length > 0 && <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-2662">
                <Eye className="h-4 w-4" data-id="element-2663" />
                View Contacts
              </button>}
          </div>
        </div>

        {loadingContacts && <div className="mt-3 flex items-center gap-2 text-sm text-gray-500" data-id="element-2664">
            <Loader2 className="h-3.5 w-3.5 animate-spin" data-id="element-2665" />
            {loadProgress}
          </div>}

        {/* Quick stats when we have matches */}
        {matchingContacts.length > 0 && <div className="mt-4 grid grid-cols-3 gap-4" data-id="element-2666">
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-2667">
              <p className="text-2xl font-bold text-gray-900" data-id="element-2668">
                {matchingContacts.length}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2669">Total Matches</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-2670">
              <p className="text-2xl font-bold text-gray-900" data-id="element-2671">
                {matchingContacts.filter(c => c.Email).length}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2672">With Email</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center" data-id="element-2673">
              <p className="text-2xl font-bold text-gray-900" data-id="element-2674">
                {new Set(matchingContacts.map(c => c.Sub_Region || c.Region).filter(Boolean)).size}
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-2675">Regions</p>
            </div>
          </div>}
      </div>

      {/* Contact Preview Panel */}
      {showPreview && <ContactPreviewPanel contacts={matchingContacts} onClose={() => setShowPreview(false)} data-id="element-2676" />}
    </div>;
}